import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faArrowLeft, faTimes, faUndo, faList, faClipboard, faHistory, faUser, faPlus, faCut, faClipboardList, faChartLine, faArchive, faFolderOpen, faUserTie } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { openWhatsapp } from '../../utils/whatsapp';
import { useAuth } from './AuthContext';
import Select from 'react-select';
import CarTimeline from './car/timeline/CarTimeline';
import AddTimelineNote from './car/timeline/AddTimelineNote';
import CarDrivers from './car/drivers/CarDrivers';
import CarInfo from './car/summary/CarInfo';
import InspectionsSummary from './car/summary/InspectionsSummary';
import DriverInfo from './car/summary/DriverInfo';
import LastTransaction from './car/summary/LastTransaction';
import FinancialInfo from './car/summary/FinancialInfo';
import InsuranceInfo from './car/summary/InsuranceInfo';
import Statement from './car/statement/Statement';
import InspectionsPanel from './car/inspections/InspectionsPanel';
import CarJobCardsList from './car/jobCards/CarJobCardsList';
import ExecutiveSummary from './car/summary/ExecutiveSummary';
import EntityDocs from './documents/EntityDocs';
import AccountManagerHistory from './car/accountManager/AccountManagerHistory';
import ConfirmModal from '../common/ConfirmModal';


const { rootUrl } = require('../../config/config');

const CarTransactions = () => {
    const { plate_no } = useParams();
    const navigate = useNavigate();
    const [carLoading, setCarLoading] = useState(true);
    const [isSharing, setIsSharing] = useState(false);
    const [carDetails, setCarDetails] = useState([]);
    const [insurance, setInsurance] = useState(null);
    // statement date/filter state moved into Statement component
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [statementRefreshKey, setStatementRefreshKey] = useState(0);
    const [splitModalOpen, setSplitModalOpen] = useState(false);
    const [selectedTransForSplit, setSelectedTransForSplit] = useState(null);
    const [splitForm, setSplitForm] = useState({ newType: '', splitAmount: '' });
    const [splitLoading, setSplitLoading] = useState(false);
    const [reversalModalOpen, setReversalModalOpen] = useState(false);
    const [reversalReason, setReversalReason] = useState('');
    const [reversalLoading, setReversalLoading] = useState(false);
    const [reversalError, setReversalError] = useState(null);
    const [selectedTransForReversal, setSelectedTransForReversal] = useState(null);
    const [splitError, setSplitError] = useState(null);
    const { user, token } = useAuth();
    const [permissions, setPermissions] = useState([]);
    // statement filter state moved into Statement component
    const [inspections, setInspections] = useState([]);
    const [inspectionsLoading, setInspectionsLoading] = useState(false);
    const [driverDetails, setDriverDetails] = useState(null);
    const [driverLoading, setDriverLoading] = useState(false);
    const [ownerDetails, setOwnerDetails] = useState(null);
    const [ownerLoading, setOwnerLoading] = useState(false);
    const [lastPayment, setLastPayment] = useState(null);
    const [lastPaymentLoading, setLastPaymentLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('transactions');
    const [timelineModalOpen, setTimelineModalOpen] = useState(false);
    const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteConfirmValue, setDeleteConfirmValue] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [error, setError] = useState(null);

    // Archive / Restore modal state
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [archiveReason, setArchiveReason] = useState('');
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [archiveError, setArchiveError] = useState('');
    const [restoreModalOpen, setRestoreModalOpen] = useState(false);
    const [restoreReason, setRestoreReason] = useState('');
    const [restoreLoading, setRestoreLoading] = useState(false);
    const [restoreError, setRestoreError] = useState('');

    const handleArchiveCar = async () => {
        if (!archiveReason.trim()) {
            setArchiveError('Archive reason is required.');
            return;
        }
        setArchiveLoading(true);
        setArchiveError('');
        try {
            await axios.post(`${rootUrl}/cars/${plate_no}/archive`, {
                archive_reason: archiveReason,
                archived_by: user?.username || user?.email || 'system'
            }, { headers: { Authorization: `Bearer ${token}` } });
            setArchiveModalOpen(false);
            fetchCarData();
        } catch (err) {
            setArchiveError(err.response?.data?.message || 'Failed to archive vehicle.');
        } finally {
            setArchiveLoading(false);
        }
    };

    const handleRestoreCar = async () => {
        if (!restoreReason.trim()) {
            setRestoreError('Restore reason is required.');
            return;
        }
        setRestoreLoading(true);
        setRestoreError('');
        try {
            await axios.post(`${rootUrl}/cars/${plate_no}/restore`, {
                restore_reason: restoreReason,
                restored_by: user?.username || user?.email || 'system'
            }, { headers: { Authorization: `Bearer ${token}` } });
            setRestoreModalOpen(false);
            fetchCarData();
        } catch (err) {
            setRestoreError(err.response?.data?.message || 'Failed to restore vehicle.');
        } finally {
            setRestoreLoading(false);
        }
    };
    // Weekly date helpers and statement filter state have been moved into the Statement component

    // Fetch user permissions
    useEffect(() => {
        if (!token) return;
        fetch(`${rootUrl}/api/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(j => setPermissions(j.data || []))
            .catch(() => setPermissions([]));
    }, [token]);

    const fetchCarData = async () => {
        try {
            const token = localStorage.getItem('authToken') || 'default-token';

            const [carResponse, typesResponse, insuranceResponse] = await Promise.all([
                axios.get(`${rootUrl}/cars/${plate_no}`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${rootUrl}/payments/types/car`, { headers: { Authorization: `Bearer ${token}` } }),
                // Use the server-mounted route `/cars/insurance` and handle the { success, data } wrapper
                axios.get(`${rootUrl}/cars/insurance/plate/${encodeURIComponent(plate_no)}/current`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { success: false, data: null } })),
            ]);

            console.log('🚗 Car Response Data:', carResponse.data);
            console.log('🚗 Car Response Data Type:', typeof carResponse.data);
            console.log('🚗 Car Response Data Keys:', carResponse.data ? Object.keys(carResponse.data) : 'null');
            console.log('🚗 Driver ID in response:', carResponse.data?.driver_id);
            console.log('🚗 Driver Name in response:', carResponse.data?.driver_name);

            setCarDetails(carResponse.data);

            // Set transaction types with full data including can_split flag
            setTransactionTypes(typesResponse.data.map(item => ({
                value: item.type,
                label: item.long_desc,
                can_split: item.can_split !== false, // Default to true if not specified
                sign: item.sign,
                reversal_type: item.reversal_type
            })));

            // references will be populated when transactions are fetched

            // Normalize insurance response (supports new { success, data } wrapper)
            try {
                let insPayload = insuranceResponse && insuranceResponse.data;

                // If the API uses the wrapper { success, data }
                if (insPayload && typeof insPayload === 'object' && 'success' in insPayload) {
                    insPayload = insPayload.success ? insPayload.data : null;
                }

                // Backwards compatibility: support a few shapes
                if (Array.isArray(insPayload)) insPayload = insPayload[0];
                else if (insPayload && insPayload.record) insPayload = insPayload.record;
                else if (insPayload && insPayload.insurance) insPayload = insPayload.insurance;

                setInsurance(insPayload || null);
            } catch (e) {
                setInsurance(null);
            }

            console.log('abt actual balance', carResponse);
        } catch (error) {
            setError(error.response?.data?.message || 'Error fetching data. Please try again later.');
            console.error('Error fetching car data:', error);

        } finally {
            setCarLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchCarData();
    }, [plate_no]);

    

    const handleDeleteTransaction = async (trans) => {
        if (!trans || !trans.type || !trans.doc_no) return;
        setSelectedTransaction(trans);
        setDeleteConfirmValue('');
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const confirmDeleteTransaction = async (reason = '') => {
        if (!selectedTransaction) return;
        // require exactly 'delete'
        if ((deleteConfirmValue || '').trim() !== 'delete') {
            setDeleteError('Type delete to confirm');
            return;
        }

        try {
            setDeleteLoading(true);
            const t = localStorage.getItem('authToken') || token || 'default-token';
            const url = `${rootUrl}/cars/${encodeURIComponent(plate_no)}/transactions/${encodeURIComponent(selectedTransaction.type)}/${encodeURIComponent(selectedTransaction.doc_no)}`;
            await axios.delete(url, { headers: { Authorization: `Bearer ${t}` }, data: { reason } });
            // Refresh car data and statement
            await fetchCarData();
            setStatementRefreshKey(k => k + 1);
            setDeleteModalOpen(false);
            setSelectedTransaction(null);
        } catch (err) {
            console.error('Error deleting transaction:', err, err.response ? err.response.data : null);
            setDeleteError(err.response?.data?.message || err.message || 'Failed to delete transaction');
        } finally {
            setDeleteLoading(false);
        }
    };

    useEffect(() => {
        const fetchInspections = async () => {
            if (!plate_no) return;
            try {
                setInspectionsLoading(true);
                const token = localStorage.getItem('authToken') || 'default-token';
                const [internalResponse, governmentResponse] = await Promise.all([
                    axios.get(`${rootUrl}/cars/inspections/internal/plate/${encodeURIComponent(plate_no)}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(() => ({ data: [] })),
                    axios.get(`${rootUrl}/cars/inspections/government/plate/${encodeURIComponent(plate_no)}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(() => ({ data: [] }))
                ]);

                const allInspections = [
                    ...(internalResponse.data || []).map(inv => ({ ...inv, inspection_type: 'internal' })),
                    ...(governmentResponse.data || []).map(inv => ({ ...inv, inspection_type: 'government' }))
                ].sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date));

                setInspections(allInspections);
            } catch (error) {
                console.error('Error fetching inspections:', error);
                setInspections([]);
            } finally {
                setInspectionsLoading(false);
            }
        };

        fetchInspections();
    }, [plate_no]);

    useEffect(() => {
        console.log('🔄 Driver useEffect triggered');
        console.log('🔄 carDetails:', carDetails);
        console.log('🔄 carDetails type:', typeof carDetails);
        console.log('🔄 carDetails is array?', Array.isArray(carDetails));
        console.log('🔄 carDetails?.driver_id:', carDetails?.driver_id);
        console.log('🔄 carDetails?.driver_id type:', typeof carDetails?.driver_id);
        console.log('🔄 carDetails?.driver_id value:', carDetails?.driver_id);

        const fetchDriverDetails = async () => {
            console.log('🔍 Checking driver_id:', carDetails?.driver_id);
            console.log('🔍 Full carDetails object:', carDetails);
            console.log('🔍 carDetails keys:', carDetails ? Object.keys(carDetails) : 'null');

            // Check if driver_id exists in different possible formats
            const driverId = carDetails?.driver_id || carDetails?.driverId || carDetails?.id;
            console.log('🔍 Checking driver_id variants:', {
                driver_id: carDetails?.driver_id,
                driverId: carDetails?.driverId,
                id: carDetails?.id,
                selected: driverId
            });

            if (!driverId) {
                console.log('❌ No driver_id found in carDetails');
                console.log('❌ Available keys:', carDetails ? Object.keys(carDetails) : 'carDetails is null');
                setDriverDetails(null);
                return;
            }

            try {
                setDriverLoading(true);
                const token = localStorage.getItem('authToken') || 'default-token';

                // First try by database ID
                let url = `${rootUrl}/drivers/id_no/${driverId}`;
                console.log('📡 Attempting to fetch driver by ID from URL:', url);
                console.log('📡 Using driver_id:', driverId);

                let response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch((err) => {
                    console.error('❌ API Error (by ID):', err);
                    console.error('❌ Error response:', err.response);
                    console.error('❌ Error status:', err.response?.status);
                    console.error('❌ Error data:', err.response?.data);
                    return null;
                });

                // If response is empty or data is empty string, try querying by id_no instead
                if (!response || !response.data || (typeof response.data === 'string' && response.data.trim() === '') || (typeof response.data === 'object' && Object.keys(response.data).length === 0)) {
                    console.log('⚠️ No driver found by ID, trying to query by id_no...');
                    // Try to get all drivers and filter by id_no (since there's no direct route)
                    // Or we could use the search endpoint if it exists
                    const searchUrl = `${rootUrl}/drivers?search=${driverId}&searchBy=id_no`;
                    console.log('📡 Attempting to fetch driver by id_no from URL:', searchUrl);

                    response = await axios.get(searchUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch((err) => {
                        console.error('❌ API Error (by id_no):', err);
                        return null;
                    });

                    // If we got results, extract the first one
                    if (response?.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
                        console.log('✅ Found driver by id_no:', response.data.data[0]);
                        response = { ...response, data: response.data.data[0] };
                    } else if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                        console.log('✅ Found driver by id_no (array format):', response.data[0]);
                        response = { ...response, data: response.data[0] };
                    }
                }

                console.log('📦 Driver API Response:', response);
                console.log('📦 Driver Response Status:', response?.status);
                console.log('📦 Driver Response Data:', response?.data);
                console.log('📦 Driver Response Data Type:', typeof response?.data);
                console.log('📦 Driver Response Data Value:', JSON.stringify(response?.data));
                console.log('📦 Full Response Object Keys:', response ? Object.keys(response) : 'null');

                // Handle different response formats
                let driverData = null;

                // Check if data is a string (might be empty or need parsing)
                if (typeof response?.data === 'string') {
                    console.log('⚠️ Response data is a string');
                    if (response.data.trim() === '') {
                        console.log('⚠️ Response data is empty string - driver might not exist in database');
                        console.log('⚠️ This means result.rows[0] was undefined in the API');
                    } else {
                        console.log('⚠️ Attempting to parse string data...');
                        try {
                            driverData = JSON.parse(response.data);
                            console.log('✅ Parsed driver data:', driverData);
                        } catch (e) {
                            console.error('❌ Failed to parse string data:', e);
                        }
                    }
                }
                // Check if data is directly an object
                else if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                    driverData = response.data;
                    console.log('✅ Driver data is an object:', driverData);
                }
                // Check if data is an array (shouldn't happen but handle it)
                else if (Array.isArray(response?.data) && response.data.length > 0) {
                    driverData = response.data[0];
                    console.log('✅ Driver data found in array, using first element:', driverData);
                }
                // Check if data is nested (response.data.data)
                else if (response?.data?.data) {
                    driverData = response.data.data;
                    console.log('✅ Driver data found in response.data.data:', driverData);
                }

                // Final check - make sure we have valid driver data
                if (driverData && typeof driverData === 'object' && Object.keys(driverData).length > 0) {
                    console.log('✅ Setting driver details:', driverData);
                    console.log('✅ Driver details keys:', Object.keys(driverData));
                    setDriverDetails(driverData);
                } else {
                    console.log('❌ No valid driver data found');
                    console.log('❌ driverData value:', driverData);
                    console.log('❌ driverData type:', typeof driverData);
                    console.log('⚠️ This likely means the driver with ID', driverId, 'does not exist in the database');
                    console.log('⚠️ The API returned an empty result (result.rows[0] was undefined)');
                    setDriverDetails(null);
                }
            } catch (error) {
                console.error('❌ Error fetching driver details:', error);
                console.error('❌ Error message:', error.message);
                console.error('❌ Error stack:', error.stack);
                setDriverDetails(null);
            } finally {
                setDriverLoading(false);
            }
        };

        // Check if carDetails exists and has driver_id
        if (carDetails && (carDetails.driver_id || carDetails.driverId || carDetails.id)) {
            const driverId = carDetails.driver_id || carDetails.driverId || carDetails.id;
            console.log('✅ Driver ID found, fetching details for:', driverId);
            fetchDriverDetails();
        } else {
            console.log('⚠️ carDetails or driver_id not available yet');
            console.log('⚠️ carDetails:', carDetails);
            if (carDetails) {
                console.log('⚠️ Available properties:', Object.keys(carDetails));
            }
        }
    }, [carDetails]);

    useEffect(() => {
        const fetchOwnerDetails = async () => {
            if (!carDetails?.owner_code) {
                setOwnerDetails(null);
                return;
            }
            try {
                setOwnerLoading(true);
                const token = localStorage.getItem('authToken') || 'default-token';
                const response = await axios.get(`${rootUrl}/owners/${carDetails.owner_code}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => null);

                if (response?.data?.data) {
                    setOwnerDetails(response.data.data);
                } else if (response?.data) {
                    setOwnerDetails(response.data);
                } else {
                    setOwnerDetails(null);
                }
            } catch (error) {
                console.error('Error fetching owner details:', error);
                setOwnerDetails(null);
            } finally {
                setOwnerLoading(false);
            }
        };

        if (carDetails?.owner_code) {
            fetchOwnerDetails();
        }
    }, [carDetails?.owner_code]);

    useEffect(() => {
        const fetchLastPayment = async () => {
            if (!plate_no) return;
            try {
                setLastPaymentLoading(true);
                const token = localStorage.getItem('authToken') || 'default-token';
                // Fetch the last WAR transaction using query parameters
                const response = await axios.get(`${rootUrl}/cars/${plate_no}/trans`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        type: 'WAR',
                        order_by: 'date',
                        order_direction: 'DESC',
                        limit: 1,
                        offset: 0
                    }
                }).catch(() => ({ data: [] }));

                if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                    setLastPayment(response.data[0]);
                } else {
                    setLastPayment(null);
                }
            } catch (error) {
                console.error('Error fetching last payment:', error);
                setLastPayment(null);
            } finally {
                setLastPaymentLoading(false);
            }
        };

        if (plate_no) {
            fetchLastPayment();
        }
    }, [plate_no]);

    // Previously the parent initialized some statement defaults from localStorage;
    // that logic was moved into the Statement component when it was decoupled.

    // These handlers are no longer used because inputs use temp state and commit on blur

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            navigate('/login'); // Redirect to login if no token is found
        }
    }, [navigate]);


    // inputs now use temp state; commit on blur

    const handleViewNotes = (transaction) => {
        setSelectedTransaction(transaction);
        setShowNotesModal(true);
    };

    const handleSplitTransaction = (transaction) => {
        setSelectedTransForSplit(transaction);
        setSplitForm({ newType: '', splitAmount: '' });
        setSplitError(null);
        setSplitModalOpen(true);
    };

    const handleReverseTransaction = (transaction) => {
        setSelectedTransForReversal(transaction);
        setReversalReason('');
        setReversalError(null);
        setReversalModalOpen(true);
    };

    const performReversal = async () => {
        if (!selectedTransForReversal) return;

        if (!reversalReason || reversalReason.trim().length < 5) {
            setReversalError('A valid reason (minimum 5 characters) is required for reversal');
            return;
        }

        try {
            setReversalLoading(true);
            setReversalError(null);

            const token = localStorage.getItem('authToken') || 'default-token';
            const response = await axios.post(
                `${rootUrl}/cars/${encodeURIComponent(plate_no)}/reverse-trans/${selectedTransForReversal.type}/${selectedTransForReversal.doc_no}`,
                { reason: reversalReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                // Refresh car details to update balance
                const carResponse = await axios.get(`${rootUrl}/cars/${encodeURIComponent(plate_no)}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCarDetails(carResponse.data);

                setReversalModalOpen(false);
                alert(`Transaction reversed successfully. Reversal Doc No: ${response.data.doc_no}`);

                // Trigger refresh by reloading
                window.location.reload();
            } else {
                setReversalError(response.data.message || 'Failed to reverse transaction');
            }
        } catch (err) {
            console.error('Error reversing transaction:', err);
            setReversalError(err.response?.data?.message || err.message || 'An error occurred during reversal');
        } finally {
            setReversalLoading(false);
        }
    };

    const getSplitTypeOptions = () => {
        if (!selectedTransForSplit) return [];
        const originalType = transactionTypes.find(type => type.value === selectedTransForSplit.type);
        const originalSign = originalType ? originalType.sign : null;
        return transactionTypes.filter(type => type.can_split !== false && (!originalSign || type.sign === originalSign));
    };

    const performSplit = async () => {
        if (!selectedTransForSplit) return;

        // Validation
        if (!splitForm.newType) {
            setSplitError('Please select a transaction type');
            return;
        }

        const splitAmt = parseFloat(splitForm.splitAmount);
        if (isNaN(splitAmt) || splitAmt <= 0) {
            setSplitError('Split amount must be a positive number');
            return;
        }

        const originalAmt = parseFloat(selectedTransForSplit.amount);
        if (splitAmt >= originalAmt) {
            setSplitError('Split amount must be less than the original transaction amount');
            return;
        }

        try {
            setSplitLoading(true);
            setSplitError(null);

            const token = localStorage.getItem('authToken') || 'default-token';
            const response = await axios.post(
                `${rootUrl}/cars/${plate_no}/split-trans/${selectedTransForSplit.type}/${selectedTransForSplit.doc_no}`,
                {
                    newType: splitForm.newType,
                    splitAmount: splitAmt
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                // Refresh car data and transactions
                const carResponse = await axios.get(`${rootUrl}/cars/${plate_no}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCarDetails(carResponse.data);

                // Close modal and show success (you might want to add a toast notification here)
                setSplitModalOpen(false);
                setSplitForm({ newType: '', splitAmount: '' });
                setSelectedTransForSplit(null);

                // Trigger a re-render by updating a state value that Statement depends on
                // The Statement component will refetch transactions on mount
                window.location.reload(); // Simple refresh - alternatively use a refresh trigger
            }
        } catch (err) {
            console.error('Error splitting transaction:', err);
            setSplitError(
                err.response?.data?.message ||
                'Failed to split transaction. Please try again.'
            );
        } finally {
            setSplitLoading(false);
        }
    };

    // Statement filter manipulation moved into child component; no-op here.



    // Helper: coerce various amount shapes to a safe number
    const safeAmount = (v) => {
        if (v === null || v === undefined) return 0;
        if (typeof v === 'number') return v;
        if (typeof v === 'string') {
            const n = parseFloat(v.replace(/,/g, ''));
            return Number.isNaN(n) ? 0 : n;
        }
        if (typeof v === 'bigint') return Number(v);
        if (typeof v === 'object') {
            return safeAmount(v.value ?? v.amount ?? v.total ?? v.amt ?? 0);
        }
        return 0;
    };

    // Format amount with 2 decimals and thousand separators
    const formatAmount = (v) => {
        const n = safeAmount(v);
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    };

    const handleShare = async () => {
        if (isSharing) return;
        setIsSharing(true);
        try {
            // Find phone number: prefer driverDetails, then carDetails fields
            const phoneRaw =
                driverDetails?.phone || driverDetails?.phone_number ||
                carDetails?.driver_phone || carDetails?.driver?.phone || carDetails?.phone ||
                carDetails?.driver_phone_number || '';

            if (!phoneRaw) {
                alert('Driver phone number not found for WhatsApp share.');
                return;
            }


            // Fetch statement from server to get authoritative transactions and balance
            const token = localStorage.getItem('authToken') || 'default-token';
            const stmtRes = await axios.get(`${rootUrl}/cars/${encodeURIComponent(plate_no)}/statement`, { headers: { Authorization: `Bearer ${token}` } }).catch((e) => {
                console.error('Error fetching statement for share:', e);
                return null;
            });

            let stmt = stmtRes?.data;
            if (stmt && typeof stmt === 'object' && 'success' in stmt) {
                stmt = stmt.success ? stmt.data : null;
            }

            let txList = [];
            if (Array.isArray(stmt)) txList = stmt;
            else if (stmt?.transactions && Array.isArray(stmt.transactions)) txList = stmt.transactions;
            else if (carDetails?.transactions && Array.isArray(carDetails.transactions)) txList = carDetails.transactions;

            // sort by date desc and take last 5
            const sorted = txList.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

            const recent = sorted.length
                ? sorted.map(t => {
                    const date = t.date ? formatDate(t.date) : '';
                    const amt = formatAmount(t.amount ?? t.value ?? 0);
                    return `${date} - ${t.car_trans_type || t.type || 'Txn'}: Kshs ${amt}\n`;
                }).join('\n')
                : (lastPayment ? `${formatDate(lastPayment.date)} - Payment: Kshs ${formatAmount(lastPayment?.amount)}\n` : 'No recent transactions');

            // Determine actual balance from statement or carDetails
            const actualBalance = safeAmount(carDetails.actual_bal);

            const message = `Mini Statement for ${carDetails?.plate_no || plate_no}:\n${recent}\n\nCurrent Balance: Kshs ${formatAmount(actualBalance)}`;

            openWhatsapp(phoneRaw, message);
        } catch (err) {
            console.error('Error preparing WhatsApp share:', err);
            alert('Unable to prepare WhatsApp message.');
        } finally {
            setIsSharing(false);
        }
    };

    const formatMoney = (value) => {
        if (value === null || value === undefined || value === '-') return '-';
        return new Intl.NumberFormat('en-US').format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'N/A';
        }
    };



    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-3 sm:p-6 lg:p-8 text-sm [&_label]:text-sm [&_p]:text-sm [&_span]:text-sm [&_td]:text-sm [&_input]:text-sm [&_select]:text-sm [&_textarea]:text-sm [&_.text-base]:!text-sm [&_.text-lg]:!text-sm [&_.text-xl]:!text-base">
            <div className="max-w-full mx-auto px-4 lg:px-8">
                {/* Header */}
                <div className="text-center mb-6">
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#2a2e30] mb-2">Car Transactions</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-sm text-[#2a2e30] opacity-80">
                        Transaction history for {plate_no}
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end md:justify-between mb-4 sm:mb-6">
                    {/* Full buttons on md+ */}
                    <div className="hidden md:flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-sm hover:bg-gray-200 transition-all duration-200 flex items-center"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                            Back
                        </button>

                        <button
                            onClick={handleShare}
                            disabled={isSharing}
                            className={`px-3 py-2 bg-green-600 text-white text-sm font-semibold rounded-sm hover:bg-green-700 transition-all duration-200 flex items-center shadow-none ${isSharing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
                            {isSharing ? 'Sharing...' : 'Share'}
                        </button>

                        {/* Archive / Restore button — shows based on archived status */}
                        {carDetails && permissions.includes('cars.archive') && (
                            carDetails.archived ? (
                                <button
                                    onClick={() => { setRestoreReason(''); setRestoreError(''); setRestoreModalOpen(true); }}
                                    className="px-3 py-2 bg-green-600 text-white text-sm font-semibold rounded-sm hover:bg-green-700 transition-all duration-200 flex items-center"
                                >
                                    <FontAwesomeIcon icon={faUndo} className="mr-2" />
                                    Restore Vehicle
                                </button>
                            ) : (
                                <button
                                    onClick={() => { setArchiveReason(''); setArchiveError(''); setArchiveModalOpen(true); }}
                                    className="px-3 py-2 bg-amber-500 text-white text-sm font-semibold rounded-sm hover:bg-amber-600 transition-all duration-200 flex items-center"
                                >
                                    <FontAwesomeIcon icon={faArchive} className="mr-2" />
                                    Archive Vehicle
                                </button>
                            )
                        )}
                        {carDetails && carDetails.archived && (
                            <span className="inline-flex items-center px-3 py-1 rounded-sm text-sm font-semibold bg-amber-100 text-amber-700 border border-amber-300">
                                <FontAwesomeIcon icon={faArchive} className="mr-1" /> Archived
                            </span>
                        )}
                    </div>

                    {/* Back button and share button for small screens */}
                    <div className="md:hidden flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            aria-label="back"
                            className="p-2 rounded-sm bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5 text-gray-700" />
                        </button>

                        <button
                            onClick={handleShare}
                            disabled={isSharing}
                            aria-label="share-whatsapp"
                            className={`p-2 rounded-sm ${isSharing ? 'bg-green-600 opacity-50 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white transition-colors`}
                        >
                            <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                {/* Car Details Section */}
                {carLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-blue-500" />
                        <span className="ml-3 text-gray-500">Loading car details...</span>
                    </div>
                ) : carDetails ? (
                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
                        <h2 className="text-base font-semibold text-[#2a2e30] mb-4">Car Details</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            <FinancialInfo carDetails={carDetails} formatMoney={formatMoney} />
                            <CarInfo carDetails={carDetails} ownerLoading={ownerLoading} ownerDetails={ownerDetails} formatDate={formatDate} />
                            <InsuranceInfo insurance={insurance} formatMoney={formatMoney} formatDate={formatDate} />
                            <DriverInfo carDetails={carDetails} driverDetails={driverDetails} driverLoading={driverLoading} formatDate={formatDate} />
                            <LastTransaction carDetails={carDetails} transactionTypes={transactionTypes} lastPayment={lastPayment} lastPaymentLoading={lastPaymentLoading} formatDate={formatDate} formatMoney={formatMoney} />
                            <InspectionsSummary inspections={inspections} inspectionsLoading={inspectionsLoading} formatDate={formatDate} />
                            {/* Weekly totals moved into the Statement component (self-contained) */}
                        </div>
                    </div>) : null}

                {/* Tabs Section */}
                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
                    {/* Tab Headers */}
                    <div className="flex border-b border-gray-200 mb-4 sm:mb-6 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('transactions')}
                            aria-label="Transactions"
                            className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'transactions'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                }`}
                        >
                            <span className="md:hidden inline-block"><FontAwesomeIcon icon={faList} className="w-5 h-5" /></span>
                            <span className="hidden md:inline">Transactions</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('inspections')}
                            aria-label="Inspections"
                            className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'inspections'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                }`}
                        >
                            <span className="md:hidden inline-block"><FontAwesomeIcon icon={faClipboard} className="w-5 h-5" /></span>
                            <span className="hidden md:inline">Inspections</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('timeline')}
                            aria-label="Timeline"
                            className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'timeline'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                }`}
                        >
                            <span className="md:hidden inline-block"><FontAwesomeIcon icon={faHistory} className="w-5 h-5" /></span>
                            <span className="hidden md:inline">Timeline</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('drivers')}
                            aria-label="Drivers"
                            className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'drivers'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                }`}
                        >
                            <span className="md:hidden inline-block"><FontAwesomeIcon icon={faUser} className="w-5 h-5" /></span>
                            <span className="hidden md:inline">Drivers</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('managers')}
                            aria-label="Account Managers"
                            className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'managers'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                }`}
                        >
                            <span className="md:hidden inline-block"><FontAwesomeIcon icon={faUserTie} className="w-5 h-5" /></span>
                            <span className="hidden md:inline">Managers</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('jobcards')}
                            aria-label="Job Cards"
                            className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'jobcards'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                }`}
                        >
                            <span className="md:hidden inline-block"><FontAwesomeIcon icon={faClipboardList} className="w-5 h-5" /></span>
                            <span className="hidden md:inline">Job Cards</span>
                        </button>
                        {permissions.includes('car.transaction.exec_view') && (
                            <button
                                onClick={() => setActiveTab('summary')}
                                aria-label="Executive Summary"
                                className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'summary'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                    }`}
                            >
                                <span className="md:hidden inline-block"><FontAwesomeIcon icon={faChartLine} className="w-5 h-5" /></span>
                                <span className="hidden md:inline">Summary</span>
                            </button>
                        )}
                        <button
                            onClick={() => setActiveTab('docs')}
                            aria-label="Drive Docs"
                            className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${activeTab === 'docs'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                }`}
                        >
                            <span className="md:hidden inline-block"><FontAwesomeIcon icon={faFolderOpen} className="w-5 h-5" /></span>
                            <span className="hidden md:inline">Docs</span>
                        </button>
                    </div>

                    {/* Transactions Tab Content */}
                    {activeTab === 'transactions' && (
                        <Statement
                            plate_no={plate_no}
                            formatDate={formatDate}
                            formatMoney={formatMoney}
                            handleViewNotes={handleViewNotes}
                            handleSplitTransaction={handleSplitTransaction}
                            handleReverseTransaction={handleReverseTransaction}
                            handleDeleteTransaction={handleDeleteTransaction}
                            permissions={permissions}
                            transactionTypes={transactionTypes}
                            refreshKey={statementRefreshKey}
                        />
                    )}

                    {/* Job Cards Tab Content */}
                    {activeTab === 'jobcards' && (
                        <CarJobCardsList plate_no={plate_no} />
                    )}

                    {/* Summary Tab Content */}
                    {activeTab === 'summary' && permissions.includes('car.transaction.exec_view') && (
                        <ExecutiveSummary plate_no={plate_no} />
                    )}

                    {/* Timeline Tab Content */}
                    {activeTab === 'timeline' && (
                        <div>
                            <div className="flex flex-col md:flex-row items-center justify-center md:justify-between mb-4 gap-2">
                                <h3 className="text-base font-bold text-[#2a2e30] text-center md:text-left">Timeline</h3>
                                <div>
                                    <button
                                        onClick={() => setTimelineModalOpen(true)}
                                        aria-label="Add timeline event"
                                        className="w-9 h-9 md:w-auto md:h-auto p-0 md:px-3 md:py-2 bg-[#FF5733] text-white text-sm font-semibold rounded-full md:rounded-sm hover:bg-[#e64a2b] transition-all duration-200 flex items-center justify-center shadow-none"
                                    >
                                        <span className="md:hidden inline-block"><FontAwesomeIcon icon={faPlus} className="w-4 h-4" /></span>
                                        <span className="hidden md:inline">Add Timeline Event</span>
                                    </button>
                                </div>
                            </div>

                            <CarTimeline key={timelineRefreshKey} plate_no={plate_no} />

                            <AddTimelineNote
                                open={timelineModalOpen}
                                onClose={() => setTimelineModalOpen(false)}
                                plateNo={plate_no}
                                onSaved={() => { setTimelineModalOpen(false); setTimelineRefreshKey(k => k + 1); }}
                            />
                        </div>
                    )}

                    {/* Drivers Tab Content */}
                    {activeTab === 'drivers' && (
                        <div className="overflow-auto">
                            <CarDrivers plate_no={plate_no} />
                        </div>
                    )}

                    {activeTab === 'managers' && (
                        <AccountManagerHistory plate_no={plate_no} />
                    )}

                    {activeTab === 'inspections' && (
                        <InspectionsPanel
                            plateNo={plate_no}
                            inspections={inspections}
                            loading={inspectionsLoading}
                            formatDate={formatDate}
                        />
                    )}

                    {activeTab === 'docs' && (
                        <EntityDocs entityType="car" entityKey={plate_no} />
                    )}

                    {/* Notes Modal */}
                    {showNotesModal && selectedTransaction && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-xl shadow-none border border-gray-200 p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-base font-semibold text-[#2a2e30]">Transaction Notes</h3>
                                    <button
                                        onClick={() => setShowNotesModal(false)}
                                        className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Transaction ID:</label>
                                        <p className="text-[#2a2e30] font-mono bg-gray-100 p-3 rounded-sm">
                                            {selectedTransaction.id}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Date:</label>
                                        <p className="text-[#2a2e30]">
                                            {formatDate(selectedTransaction.date)}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Amount:</label>
                                        <p className="font-semibold text-green-600">
                                            ${parseFloat(selectedTransaction.amount || 0).toFixed(2)}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Reference:</label>
                                        <p className="text-[#2a2e30]">
                                            {selectedTransaction.your_ref || 'N/A'}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Notes:</label>
                                        <div className="bg-gray-100 p-4 rounded-sm">
                                            <p className="text-[#2a2e30] whitespace-pre-wrap">
                                                {selectedTransaction.notes || 'No notes available'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end mt-6">
                                    <button
                                        onClick={() => setShowNotesModal(false)}
                                        className="px-6 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-sm hover:bg-gray-200 transition-all duration-200"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Split Transaction Modal */}
                    {splitModalOpen && selectedTransForSplit && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-xl shadow-none border border-gray-200 p-6 max-w-2xl w-full mx-4">
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h3 className="text-base font-bold text-[#2a2e30]">Split Transaction</h3>
                                        <p className="text-sm text-gray-600 mt-1">Divide this transaction into two separate transactions</p>
                                    </div>
                                    <button
                                        onClick={() => setSplitModalOpen(false)}
                                        className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                        disabled={splitLoading}
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Original Transaction Info */}
                                <div className="bg-gray-50 rounded-sm p-4 mb-6">
                                    <h4 className="font-semibold text-[#2a2e30] mb-3">Original Transaction</h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Type:</span>
                                            <span className="ml-2 font-semibold text-[#2a2e30]">{selectedTransForSplit.type}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Doc No:</span>
                                            <span className="ml-2 font-semibold text-[#2a2e30]">{selectedTransForSplit.doc_no}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Amount:</span>
                                            <span className="ml-2 font-semibold text-green-600">
                                                KES {parseFloat(selectedTransForSplit.amount || 0).toFixed(2)}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Date:</span>
                                            <span className="ml-2 font-semibold text-[#2a2e30]">{formatDate(selectedTransForSplit.date)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Split Form */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">
                                            New Transaction Type <span className="text-red-500">*</span>
                                        </label>
                                        <Select
                                            classNamePrefix="react-select"
                                            className="w-full"
                                            options={getSplitTypeOptions()}
                                            value={getSplitTypeOptions().find(option => option.value === splitForm.newType) || null}
                                            onChange={(option) => setSplitForm({ ...splitForm, newType: option ? option.value : '' })}
                                            isDisabled={splitLoading}
                                            placeholder="Select transaction type"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">
                                            Split Amount <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            max={parseFloat(selectedTransForSplit.amount || 0) - 0.01}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-sm text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 text-right"
                                            value={splitForm.splitAmount}
                                            onChange={(e) => setSplitForm({ ...splitForm, splitAmount: e.target.value })}
                                            placeholder="0.00"
                                            disabled={splitLoading}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Remaining: KES {(parseFloat(selectedTransForSplit.amount || 0) - parseFloat(splitForm.splitAmount || 0)).toFixed(2)}
                                        </p>
                                    </div>

                                    {splitError && (
                                        <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                                            <p className="text-red-600 text-sm">{splitError}</p>
                                        </div>
                                    )}

                                    {/* Preview */}
                                    {splitForm.splitAmount && parseFloat(splitForm.splitAmount) > 0 && (
                                        <div className="bg-blue-50 rounded-sm p-4">
                                            <h5 className="font-semibold text-[#2a2e30] mb-2 text-sm">Preview</h5>
                                            <div className="space-y-1 text-sm">
                                                <p className="text-gray-700">
                                                    Original ({selectedTransForSplit.type}):
                                                    <span className="ml-2 font-semibold">
                                                        KES {(parseFloat(selectedTransForSplit.amount) - parseFloat(splitForm.splitAmount || 0)).toFixed(2)}
                                                    </span>
                                                </p>
                                                <p className="text-gray-700">
                                                    New ({splitForm.newType || '?'}):
                                                    <span className="ml-2 font-semibold text-[#FF5733]">
                                                        KES {parseFloat(splitForm.splitAmount || 0).toFixed(2)}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex justify-end gap-3 mt-6">
                                    <button
                                        onClick={() => setSplitModalOpen(false)}
                                        className="px-6 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-sm hover:bg-gray-200 transition-all duration-200"
                                        disabled={splitLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={performSplit}
                                        className="px-6 py-2 bg-[#FF5733] text-white text-sm font-semibold rounded-sm hover:bg-[#e64520] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={splitLoading || !splitForm.newType || !splitForm.splitAmount}
                                    >
                                        {splitLoading ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                                Splitting...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faCut} className="mr-2" />
                                                Split Transaction
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Reversal Modal */}
                    {reversalModalOpen && selectedTransForReversal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-xl shadow-none border border-gray-200 p-6 max-w-lg w-full">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-base font-bold text-[#2a2e30]">Reverse Transaction</h3>
                                    <button
                                        onClick={() => setReversalModalOpen(false)}
                                        className="text-gray-500 hover:text-gray-700 transition-colors"
                                        disabled={reversalLoading}
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Original Transaction Info */}
                                <div className="bg-red-50 rounded-sm p-4 mb-6 border border-red-100">
                                    <h4 className="font-semibold text-red-800 mb-3">Transaction to Reverse</h4>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-red-700">
                                        <div>Type: <span className="font-bold">{selectedTransForReversal.type}</span></div>
                                        <div>Doc No: <span className="font-bold">{selectedTransForReversal.doc_no}</span></div>
                                        <div>Amount: <span className="font-bold">KES {parseFloat(selectedTransForReversal.amount || 0).toFixed(2)}</span></div>
                                        <div>Date: <span className="font-bold">{formatDate(selectedTransForReversal.date)}</span></div>
                                    </div>
                                </div>

                                {/* Reversal Reason Form */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">
                                            Reason for Reversal <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            className="w-full px-4 py-3 border border-gray-300 rounded-sm text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 min-h-[100px]"
                                            value={reversalReason}
                                            onChange={(e) => setReversalReason(e.target.value)}
                                            placeholder="Enter detailed reason for reversal (min 5 chars)..."
                                            disabled={reversalLoading}
                                        />
                                        <p className="text-xs text-gray-500 mt-1 italic">
                                            This will be permanently recorded in the reversal transaction notes.
                                        </p>
                                    </div>

                                    {reversalError && (
                                        <div className="bg-red-100 border border-red-200 rounded-sm p-3">
                                            <p className="text-red-700 text-sm font-medium">{reversalError}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex justify-end gap-3 mt-8">
                                    <button
                                        onClick={() => setReversalModalOpen(false)}
                                        className="px-6 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-sm hover:bg-gray-200 transition-all duration-200"
                                        disabled={reversalLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={performReversal}
                                        className="px-6 py-2 bg-red-600 text-white text-sm font-semibold rounded-sm hover:bg-red-700 transition-all duration-200 flex items-center justify-center min-w-[140px]"
                                        disabled={reversalLoading || !reversalReason || reversalReason.trim().length < 5}
                                    >
                                        {reversalLoading ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faUndo} className="mr-2" />
                                                Confirm Reversal
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>



            <ConfirmModal
                isOpen={deleteModalOpen}
                title="Confirm Delete"
                description={selectedTransaction ? `You are about to archive transaction ${selectedTransaction.type} ${selectedTransaction.doc_no} for ${plate_no}. This action is destructive and will move the record to the archive table.` : ''}
                typedConfirmLabel={'Type "delete" to confirm'}
                typedConfirmValue={deleteConfirmValue}
                onTypedConfirmChange={(v) => { setDeleteConfirmValue(v); setDeleteError(null); }}
                onCancel={() => { setDeleteModalOpen(false); setSelectedTransaction(null); }}
                onConfirm={() => confirmDeleteTransaction('User confirmed delete')}
                confirmText={'Delete'}
                loading={deleteLoading}
                error={deleteError}
            />

            {/* Archive Vehicle Modal */}
            {archiveModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-xl shadow-none border border-gray-200 p-6 w-full max-w-md mx-4">
                        <h2 className="text-base font-bold text-[#2a2e30] mb-1">Archive Vehicle</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Archiving <strong>{plate_no}</strong> will hide it from the active fleet and block new transactions and driver assignments.
                        </p>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Reason for archiving <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] resize-none"
                            rows={3}
                            placeholder="e.g. Vehicle sold, written off, decommissioned..."
                            value={archiveReason}
                            onChange={(e) => setArchiveReason(e.target.value)}
                        />
                        {archiveError && <p className="text-red-500 text-sm mt-1">{archiveError}</p>}
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setArchiveModalOpen(false)}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-4 py-2"
                                disabled={archiveLoading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleArchiveCar}
                                className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={archiveLoading}
                            >
                                {archiveLoading ? 'Archiving...' : 'Archive Vehicle'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Restore Vehicle Modal */}
            {restoreModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-xl shadow-none border border-gray-200 p-6 w-full max-w-md mx-4">
                        <h2 className="text-base font-bold text-[#2a2e30] mb-1">Restore Vehicle</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Restoring <strong>{plate_no}</strong> will return it to the active fleet.
                        </p>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Reason for restoring <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] resize-none"
                            rows={3}
                            placeholder="e.g. Returned to service, error correction..."
                            value={restoreReason}
                            onChange={(e) => setRestoreReason(e.target.value)}
                        />
                        {restoreError && <p className="text-red-500 text-sm mt-1">{restoreError}</p>}
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setRestoreModalOpen(false)}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-4 py-2"
                                disabled={restoreLoading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRestoreCar}
                                className="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={restoreLoading}
                            >
                                {restoreLoading ? 'Restoring...' : 'Restore Vehicle'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CarTransactions;
